import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Bot, Play, ShieldCheck, GitBranch, CheckCircle2, XCircle, Search, ArrowUpCircle } from "lucide-react";

const SCENARIOS = [
  "new_valid_company", "existing_company", "duplicate_company", "exact_abn_match", "exact_domain_match",
  "ambiguous_fuzzy", "missing_abn", "missing_website", "multiple_evidence", "claim_safe_absence",
  "prohibited_source", "prohibited_icn", "malformed_contract", "inject_score", "stale_event",
  "conflicting_info", "partial_failure",
];

const chip = (v: string) => {
  const m: Record<string, string> = {
    valid: "bg-teal-100 text-teal-800", invalid: "bg-red-100 text-red-700",
    allowed: "bg-teal-100 text-teal-800", blocked: "bg-red-100 text-red-700", requires_review: "bg-amber-100 text-amber-800",
    matched: "bg-teal-100 text-teal-800", new_candidate: "bg-sky-100 text-sky-800", ambiguous: "bg-amber-100 text-amber-800",
    ready: "bg-teal-100 text-teal-800", promoted: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700",
    approved: "bg-teal-100 text-teal-800", unreviewed: "bg-slate-100 text-slate-700", needs_research: "bg-amber-100 text-amber-800",
  };
  return m[v] || "bg-slate-100 text-slate-700";
};

export default function AgentControlCentre() {
  const { user } = useAuth();
  const canReview = ["admin", "analyst"].includes(user?.role);
  const [agents, setAgents] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [findings, setFindings] = useState<any[]>([]);
  const [health, setHealth] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [healthDetail, setHealthDetail] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [scenario, setScenario] = useState("new_valid_company");
  const [running, setRunning] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [selAgent, setSelAgent] = useState("synthetic_defence_discovery");
  const [didgOpen, setDidgOpen] = useState(false);
  const [didgForm, setDidgForm] = useState<any>({
    format: "csv", content: "", content_base64: "", filename: "",
    source_title: "DIDG Program: Recipients of grants – November 2025 to June 2026",
    coverage_period: "November 2025 to June 2026", retrieved_at: "",
  });

  const load = async () => {
    try {
      const [a, m, f, h, s] = await Promise.all([
        api.get("/agents"), api.get("/agents/metrics"),
        api.get(`/agents/findings?include_duplicates=${showDuplicates}`),
        api.get("/agents/health"), api.get("/agents/health-summary"),
      ]);
      setAgents(a.data); setMetrics(m.data); setFindings(f.data); setHealth(h.data); setSummary(s.data);
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [showDuplicates]);

  const runAgent = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/agents/${selAgent}/run`, { scenario, trigger_type: "manual" });
      toast.success(`Run ${r.data.status} — ${r.data.findings_generated} finding(s), ${r.data.findings_rejected} rejected`);
      await load();
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const openFinding = async (id: string) => {
    try { const r = await api.get(`/agents/findings/${id}`); setDetail(r.data); }
    catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const review = async (decision: string) => {
    try {
      await api.post(`/agents/findings/${detail.id}/review`, { decision, notes: `${decision} via console` });
      toast.success(`Finding ${decision}`); await openFinding(detail.id); await load();
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const resolveEntity = async (companyId: string | null) => {
    try {
      await api.post(`/agents/findings/${detail.id}/resolve-entity`, { company_id: companyId, reason: "analyst decision via console" });
      toast.success("Entity resolved"); await openFinding(detail.id); await load();
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const promote = async () => {
    try {
      const r = await api.post(`/agents/findings/${detail.id}/promote`);
      toast.success(`Promoted → evidence created (${r.data.evidence_created.length}). Score recalculated: ${r.data.opportunity_score}`);
      await openFinding(detail.id); await load();
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const onPdfFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const b64 = res.includes(",") ? res.split(",")[1] : res;
      setDidgForm((prev: any) => ({ ...prev, content_base64: b64, filename: file.name }));
      toast.success(`Loaded ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const submitDidg = async () => {
    if (didgForm.format === "pdf" && !didgForm.content_base64) { toast.error("Choose a PDF file first"); return; }
    if (didgForm.format !== "pdf" && !didgForm.content.trim()) { toast.error("Paste the saved CSV/JSON content"); return; }
    try {
      const r = await api.post("/agents/didg/ingest-document", didgForm);
      if (r.data.reingest_of_document) {
        toast.warning(`This exact document was already ingested${r.data.reingest_first_seen ? ` on ${new Date(r.data.reingest_first_seen).toLocaleDateString()}` : ""}. The ${r.data.findings_generated} finding(s) are flagged as duplicates and hidden from the review queue.`);
      } else {
        toast.success(`DIDG: parsed ${r.data.recipients_parsed}, ${r.data.findings_generated} finding(s) staged`);
      }
      setDidgOpen(false); await load();
    } catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const openHealth = async (id: string) => {
    try { const r = await api.get(`/agents/health/${id}`); setHealthDetail(r.data); }
    catch (e: any) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const rag = (s: string) => (({ GREEN: "bg-emerald-500", AMBER: "bg-amber-500", RED: "bg-red-500", GREY: "bg-slate-400" } as any)[s] || "bg-slate-300");

  return (
    <div className="space-y-6" data-testid="agent-control-centre">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0B1C3D] flex items-center justify-center"><Bot className="h-5 w-5 text-teal-400" /></div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Agent Control Centre</h1>
          <p className="text-sm text-slate-500">Synthetic Data — Non-production. Findings are staged intelligence, never auto-evidence.</p>
        </div>
      </div>

      {summary && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 p-3 rounded-lg bg-slate-50 border" data-testid="health-summary">
          <div className="flex items-center gap-2 text-sm" data-testid="agent-health-summary">
            <span className="font-semibold text-slate-700">Agent Health:</span>
            <span className="text-emerald-600 font-semibold">{summary.agent_health.GREEN} Green</span><span className="text-slate-300">·</span>
            <span className="text-amber-600 font-semibold">{summary.agent_health.AMBER} Amber</span><span className="text-slate-300">·</span>
            <span className="text-red-600 font-semibold">{summary.agent_health.RED} Red</span>
          </div>
          <div className="flex items-center gap-2 text-sm" data-testid="source-health-summary">
            <span className="font-semibold text-slate-700">Source Health:</span>
            <span className="text-emerald-600 font-semibold">{summary.source_health.GREEN} Green</span><span className="text-slate-300">·</span>
            <span className="text-amber-600 font-semibold">{summary.source_health.AMBER} Amber</span><span className="text-slate-300">·</span>
            <span className="text-red-600 font-semibold">{summary.source_health.RED} Red</span><span className="text-slate-300">·</span>
            <span className="text-slate-500 font-semibold">{summary.source_health.GREY} Grey</span>
          </div>
          <span className="text-xs text-slate-400">Production only — synthetic/test excluded</span>
        </div>
      )}

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="agent-metrics">
          {[["Runs", metrics.total_runs], ["OK", metrics.successful_runs], ["Findings", metrics.findings_generated],
            ["Rejected", metrics.findings_rejected], ["Promoted", metrics.findings_promoted],
            ["Duplicates", metrics.duplicates_detected], ["Entity cases", metrics.entity_resolution_cases]].map(([l, v]) => (
            <Card key={l as string} className="p-3"><div className="text-xs text-slate-500">{l}</div><div className="text-xl font-bold text-slate-900">{v as number}</div></Card>
          ))}
        </div>
      )}

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-700">Registered Agents</div>
            <div className="text-xs text-slate-400">Functional agents. Each owns one or more source connectors (Agent → Connector → Run → Finding → Evidence).</div>
          </div>
          {canReview && <Button variant="outline" size="sm" onClick={() => setDidgOpen(true)} data-testid="didg-upload-btn">Upload DIDG saved document</Button>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agents-table">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2">Agent</th><th>Type</th><th>Lifecycle</th><th>Version</th><th>Findings</th><th>Promoted</th><th>Errors</th>
            </tr></thead>
            <tbody>
              {agents.filter((a) => a.tier === "production").map((a) => (
                <tr key={a.agent_id} className="border-b last:border-0" data-testid={`agent-row-${a.agent_id}`}>
                  <td className="py-2 font-medium text-slate-900">{a.agent_name}</td>
                  <td><Badge variant="outline">{a.agent_type}</Badge></td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${a.status === "enabled" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{a.status === "enabled" ? "production / enabled" : "future / not configured"}</span></td>
                  <td>{a.current_agent_version}</td>
                  <td>{a.findings_count}</td><td>{a.promoted_count}</td><td>{a.errors_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4" data-testid="connector-health">
        <div className="text-sm font-semibold text-slate-700 mb-1">Connector / Source Health</div>
        <div className="text-xs text-slate-400 mb-3">Each row is a source connector and its parent agent. RAG derived from system state & recent activity — separate from policy. GREY = test/future/not configured.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="health-table">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2">Health</th><th>Agent</th><th>Connector</th><th>Mode</th><th>Policy</th><th>Live retrieval</th><th>Last success</th><th>Last result</th><th></th>
            </tr></thead>
            <tbody>
              {health.map((h) => (
                <tr key={h.connector_id} className="border-b last:border-0" data-testid={`health-row-${h.connector_id}`}>
                  <td className="py-2"><span className="inline-flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${rag(h.health)}`} /> <span className="text-xs font-semibold">{h.health}</span></span></td>
                  <td className="text-slate-700">{h.agent_name}{h.tier === "synthetic" && <Badge variant="outline" className="ml-1 text-[10px]">test</Badge>}</td>
                  <td className="font-medium text-slate-900">{h.connector_name}</td>
                  <td className="text-slate-600">{h.mode_label}</td>
                  <td className="text-slate-600">{h.policy_state}</td>
                  <td className="text-slate-600">{h.live_retrieval_approval}</td>
                  <td className="text-slate-500 text-xs">{h.last_success_check ? new Date(h.last_success_check).toLocaleString() : "—"}</td>
                  <td className="text-slate-600">{h.last_result || "—"}</td>
                  <td><Button variant="outline" size="sm" onClick={() => openHealth(h.connector_id)} data-testid={`health-detail-${h.connector_id}`}>Why?</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4" data-testid="test-simulator">
        <div className="text-sm font-semibold text-slate-700 mb-1">Test / Simulator</div>
        <div className="text-xs text-slate-400 mb-3">Synthetic agents for Phase 02A pipeline testing — not production sources and excluded from production health totals.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="synthetic-agents-table">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2">Agent</th><th>Type</th><th>Status</th><th>Version</th><th>Prompt</th><th>Findings</th><th>Promoted</th><th>Errors</th>
            </tr></thead>
            <tbody>
              {agents.filter((a) => a.tier === "synthetic").map((a) => (
                <tr key={a.agent_id} className="border-b last:border-0" data-testid={`agent-row-${a.agent_id}`}>
                  <td className="py-2 font-medium text-slate-900">{a.agent_name}</td>
                  <td><Badge variant="outline">{a.agent_type}</Badge></td>
                  <td><span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{a.status}</span></td>
                  <td>{a.current_agent_version}</td><td>{a.current_prompt_version}</td>
                  <td>{a.findings_count}</td><td>{a.promoted_count}</td><td>{a.errors_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canReview && (
          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t">
            <div><div className="text-xs text-slate-500 mb-1">Simulator agent</div>
              <Select value={selAgent} onValueChange={setSelAgent}>
                <SelectTrigger className="w-64" data-testid="run-agent-select"><SelectValue /></SelectTrigger>
                <SelectContent>{agents.filter((a) => a.tier === "synthetic").map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.agent_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><div className="text-xs text-slate-500 mb-1">Synthetic scenario</div>
              <Select value={scenario} onValueChange={setScenario}>
                <SelectTrigger className="w-56" data-testid="run-scenario-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SCENARIOS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button onClick={runAgent} disabled={running} data-testid="run-agent-btn" className="bg-[#0B1C3D]">
              <Play className="h-4 w-4 mr-1" />{running ? "Running…" : "Run synthetic agent"}</Button>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-700">Finding Review Queue</div>
          <div className="flex items-center gap-2">
            {metrics && metrics.duplicates_detected > 0 && !showDuplicates && (
              <span className="text-xs text-slate-400" data-testid="duplicates-hidden-note">{metrics.duplicates_detected} duplicate(s) hidden</span>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowDuplicates((v) => !v)} data-testid="toggle-duplicates-btn">
              {showDuplicates ? "Hide duplicates" : "Show duplicates"}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="findings-table">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2">Organisation</th><th>Signal</th><th>Validation</th><th>Policy</th><th>Entity</th><th>Dup</th><th>Review</th><th>Promotion</th><th></th>
            </tr></thead>
            <tbody>
              {findings.map((f) => (
                <tr key={f.id} className="border-b last:border-0" data-testid={`finding-row-${f.id}`}>
                  <td className="py-2 font-medium text-slate-900">{f.organisation?.name || "—"}</td>
                  <td className="text-xs text-slate-500 max-w-[280px]">
                    <div className="truncate" title={f.discovery_signal?.summary || ""}>{f.discovery_signal?.summary || "—"}</div>
                    {f.discovery_signal?.event_date && <div className="text-slate-400">{f.discovery_signal.event_date}</div>}
                  </td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${chip(f.validation_status)}`}>{f.validation_status}</span></td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${chip(f.policy_status)}`}>{f.policy_status}</span></td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${chip(f.entity_resolution_status)}`}>{f.entity_resolution_status}</span></td>
                  <td>{f.is_duplicate ? <Badge variant="destructive">dup</Badge> : "—"}</td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${chip(f.review_status)}`}>{f.review_status}</span></td>
                  <td><span className={`px-2 py-0.5 rounded text-xs ${chip(f.promotion_status)}`}>{f.promotion_status}</span></td>
                  <td><Button variant="outline" size="sm" onClick={() => openFinding(f.id)} data-testid={`inspect-finding-${f.id}`}>Inspect</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="finding-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Search className="h-4 w-4" />{detail.organisation?.name}</DialogTitle>
                <DialogDescription>Synthetic Data — Non-production. Agent {detail.agent_id} · v{detail.agent_version} · prompt {detail.prompt_version}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs ${chip(detail.validation_status)}`}>validation: {detail.validation_status}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${chip(detail.policy_status)}`}>policy: {detail.policy_status}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${chip(detail.entity_resolution_status)}`}>entity: {detail.entity_resolution_status}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${chip(detail.promotion_status)}`}>promotion: {detail.promotion_status}</span>
                </div>
                {detail.rejection_reason && <div className="p-2 bg-red-50 text-red-700 rounded text-xs">{detail.rejection_reason}</div>}
                {detail.discovery_signal && (
                  <div><div className="font-semibold text-slate-700">Discovery signal</div>
                    <div className="text-slate-600">{detail.discovery_signal.signal_type} · {detail.discovery_signal.event_date}</div>
                    <div className="text-slate-600">{detail.discovery_signal.summary}</div></div>
                )}
                <div><div className="font-semibold text-slate-700">Evidence</div>
                  {(detail.evidence || []).map((e: any, i: number) => (
                    <div key={i} className="border rounded p-2 mt-1">
                      <div className="text-slate-800">{e.source_title} <Badge variant="outline">{e.evidence_class}</Badge></div>
                      <div className="text-slate-500 text-xs break-all">{e.source_url}</div>
                      <div className="text-slate-600 text-xs mt-1">{e.evidence_text}</div>
                    </div>
                  ))}</div>
                {detail.agent_analysis && (
                  <div><div className="font-semibold text-slate-700">Agent interpretation (not Radar Evidence Confidence)</div>
                    <div className="text-slate-600">{detail.agent_analysis.defence_relevance}</div>
                    <div className="text-xs text-slate-500">Agent confidence: {detail.agent_analysis.confidence}/100</div></div>
                )}
                {detail.entity_case && (
                  <div className="border rounded p-2 bg-amber-50">
                    <div className="font-semibold text-amber-800">Ambiguous entity — analyst decision required</div>
                    {(detail.entity_case.match_features || []).map((m: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-xs mt-1">
                        <span>{m.company_name} ({m.feature}{m.similarity ? ` ${m.similarity}` : ""})</span>
                        {canReview && <Button size="sm" variant="outline" onClick={() => resolveEntity(m.company_id)} data-testid={`resolve-match-${m.company_id}`}>Match this</Button>}
                      </div>
                    ))}
                    {canReview && <Button size="sm" variant="outline" className="mt-2" onClick={() => resolveEntity(null)} data-testid="resolve-create-new">Create new candidate</Button>}
                  </div>
                )}
              </div>
              {canReview && (
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" onClick={() => review("approved")} data-testid="approve-finding-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                  <Button variant="outline" onClick={() => review("needs_research")} data-testid="needs-research-btn"><GitBranch className="h-4 w-4 mr-1" />Needs Research</Button>
                  <Button variant="outline" onClick={() => review("rejected")} data-testid="reject-finding-btn"><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                  <Button className="bg-[#0B1C3D]" onClick={promote} data-testid="promote-finding-btn"><ArrowUpCircle className="h-4 w-4 mr-1" />Promote Evidence</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!healthDetail} onOpenChange={(o) => !o && setHealthDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="health-dialog">
          {healthDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><span className={`h-3 w-3 rounded-full ${rag(healthDetail.health)}`} />{healthDetail.connector_name || healthDetail.name} — {healthDetail.health}</DialogTitle>
                <DialogDescription>Agent: {healthDetail.agent_name} · Connector health is derived from system state, not manually assigned. Kept separate from policy.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div><div className="font-semibold text-slate-700">Why this status</div>
                  <ul className="list-disc pl-5 text-slate-600">{(healthDetail.reasons || []).map((r: string, i: number) => <li key={i}>{r}</li>)}</ul></div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <div>Mode: <b>{healthDetail.execution_mode}</b></div>
                  <div>Policy: <b>{healthDetail.policy_state}</b></div>
                  <div>Live retrieval: <b>{healthDetail.live_retrieval_approval}</b></div>
                  <div>Next scheduled: <b>{healthDetail.next_scheduled_run || "manual only"}</b></div>
                  <div>Last attempt: <b>{healthDetail.last_attempt_check ? new Date(healthDetail.last_attempt_check).toLocaleString() : "—"}</b></div>
                  <div>Last discovery: <b>{healthDetail.last_discovery ? new Date(healthDetail.last_discovery).toLocaleString() : "—"}</b></div>
                  <div className="col-span-2">Latest warning/error: <b>{healthDetail.latest_warning_error || "none"}</b></div>
                </div>
                <div><div className="font-semibold text-slate-700">Recent run history</div>
                  <table className="w-full text-xs mt-1"><tbody>
                    {(healthDetail.runs || []).map((r: any) => (
                      <tr key={r.run_id} className="border-b last:border-0">
                        <td className="py-1">{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</td>
                        <td>{r.status}</td><td>gen {r.findings_generated ?? 0}</td><td>rej {r.findings_rejected ?? 0}</td>
                        <td className="text-red-600">{(r.errors || [])[0] || ""}</td>
                      </tr>
                    ))}
                  </tbody></table></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={didgOpen} onOpenChange={setDidgOpen}>
        <DialogContent className="max-w-lg" data-testid="didg-dialog">
          <DialogHeader>
            <DialogTitle>Ingest DIDG saved document</DialogTitle>
            <DialogDescription>Saved-document only — no automated retrieval of defence.gov.au. Upload a saved PDF of the public DIDG recipient list, or paste a saved CSV/JSON export. Every recipient becomes a review-gated Discovery Signal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-2">
              <Select value={didgForm.format} onValueChange={(v) => setDidgForm({ ...didgForm, format: v })}>
                <SelectTrigger className="w-32" data-testid="didg-format"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="csv">CSV</SelectItem><SelectItem value="json">JSON</SelectItem></SelectContent>
              </Select>
              <Input value={didgForm.retrieved_at} onChange={(e) => setDidgForm({ ...didgForm, retrieved_at: e.target.value })} placeholder="Retrieved date (YYYY-MM-DD)" data-testid="didg-retrieved" />
            </div>
            <Input value={didgForm.source_title} onChange={(e) => setDidgForm({ ...didgForm, source_title: e.target.value })} placeholder="Source document title" data-testid="didg-title" />
            <Input value={didgForm.coverage_period} onChange={(e) => setDidgForm({ ...didgForm, coverage_period: e.target.value })} placeholder="Coverage period" data-testid="didg-period" />
            {didgForm.format === "pdf" ? (
              <div className="space-y-1">
                <input type="file" accept="application/pdf,.pdf" onChange={onPdfFile} data-testid="didg-file"
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-[#0B1C3D] file:px-3 file:py-1.5 file:text-white" />
                {didgForm.filename && <div className="text-xs text-teal-700">Selected: {didgForm.filename}</div>}
                <div className="text-xs text-slate-400">Recipient tables are extracted automatically; the original file is hashed for provenance.</div>
              </div>
            ) : (
              <Textarea rows={8} value={didgForm.content} onChange={(e) => setDidgForm({ ...didgForm, content: e.target.value })}
                placeholder={"name,stream,project_title,amount,state,purpose,abn\nAcme Pty Ltd,DIDG Stream A,Sensor uplift,250000,SA,Develop sensors,"} data-testid="didg-content" />
            )}
          </div>
          <DialogFooter>
            <Button className="bg-[#0B1C3D]" onClick={submitDidg} data-testid="didg-submit">Ingest document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
